import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Switch } from './ui/switch';
import { useAuth } from '../context/AuthContext';
import { useApiBase } from '../hooks/useApiBase';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, CheckCircle2, Loader2, User } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import {
  DEFAULT_LOGGING_PREFERENCES,
  resolveLoggingPreferences,
  resolveRecordViewPreferences,
  mergeLoggingPreferenceUpdate,
  type LoggingPreferences,
  type UserPreferences,
} from '../utils/userPreferences';

type UserProfileDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function hydrateUserPreferences(raw: unknown): UserPreferences {
  const base =
    raw && typeof raw === 'object'
      ? (raw as UserPreferences)
      : { logging: { ...DEFAULT_LOGGING_PREFERENCES } };
  const logging = resolveLoggingPreferences(base);
  const recordViews = resolveRecordViewPreferences(base);
  const hydrated: UserPreferences = {
    ...(base || {}),
    logging,
  };
  if (Object.keys(recordViews).length) {
    hydrated.recordViews = recordViews;
  }
  return hydrated;
}

export default function UserProfileDialog({ open, onOpenChange }: UserProfileDialogProps) {
  const { user, updateUser, isAdmin } = useAuth();
  const apiBase = useApiBase();
  
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [display_name, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [job_title, setJobTitle] = useState('');
  const [address_line1, setAddressLine1] = useState('');
  const [address_line2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postal_code, setPostalCode] = useState('');
  const [country, setCountry] = useState('USA');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [preferences, setPreferences] = useState<UserPreferences>(() =>
    hydrateUserPreferences(undefined)
  );
  const loggingPrefs = useMemo(() => resolveLoggingPreferences(preferences), [preferences]);
  const handleLoggingToggle = useCallback(
    (key: keyof LoggingPreferences, value: boolean) => {
      setPreferences((prev) =>
        mergeLoggingPreferenceUpdate(
          prev,
          {
            [key]: value,
          } as Partial<LoggingPreferences>
        )
      );
    },
    []
  );
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load user data when dialog opens
  useEffect(() => {
    if (open && user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
      setUsername(user.username || '');
      setDisplayName(user.display_name || '');
      setPhone(user.phone || '');
      setJobTitle(user.job_title || '');
      setAddressLine1(user.address_line1 || '');
      setAddressLine2(user.address_line2 || '');
      setCity(user.city || '');
      setState(user.state || '');
      setPostalCode(user.postal_code || '');
      setCountry(user.country || 'USA');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage(null);
      // Load preferences from user custom_fields
      const customFields = (user.custom_fields || {}) as Record<string, unknown>;
      setPreferences(hydrateUserPreferences(customFields.preferences));
    }
  }, [open, user]);

  const loadUserData = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const url = `${apiBase}/api/users/${user.id}`;
      const response = await authFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      // Handle status 0 (network error, CORS issue, etc.)
      if (response.status === 0) {
        setMessage({ 
          type: 'error', 
          text: 'Network error: Unable to reach the server. Please check your connection and ensure the backend is running.' 
        });
        return;
      }
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        setMessage({ 
          type: 'error', 
          text: `Failed to load user data: ${response.status} ${response.statusText || errorText}` 
        });
        return;
      }
      
      const data = await response.json().catch(() => ({ status: 'error', message: 'Invalid JSON response' }));
      
      if (data.status !== 'success' || !data.user) {
        setMessage({ 
          type: 'error', 
          text: data.message || 'Failed to load user data' 
        });
        return;
      }
      
      const userData = data.user;
      setFirstName(userData.first_name || '');
      setLastName(userData.last_name || '');
      setEmail(userData.email || '');
      setUsername(userData.username || '');
      setDisplayName(userData.display_name || '');
      setPhone(userData.phone || '');
      setJobTitle(userData.job_title || '');
      setAddressLine1(userData.address_line1 || '');
      setAddressLine2(userData.address_line2 || '');
      setCity(userData.city || '');
      setState(userData.state || '');
      setPostalCode(userData.postal_code || '');
      setCountry(userData.country || 'USA');
      // Load preferences from custom_fields
      const customFields = userData.custom_fields || {};
      setPreferences(hydrateUserPreferences(customFields.preferences));
      setMessage(null); // Clear any previous errors
    } catch (err) {
      console.error('Failed to load user data:', err);
      setMessage({ 
        type: 'error', 
        text: err instanceof Error ? err.message : 'Failed to load user data. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, user?.id]);

  useEffect(() => {
    if (open) {
      loadUserData();
    }
  }, [open, loadUserData]);

  const handleSave = useCallback(async () => {
    if (!user?.id) return;
    
    // Validate passwords if any password field is filled
    if (currentPassword || newPassword || confirmPassword) {
      if (!currentPassword) {
        setMessage({ type: 'error', text: 'Current password is required to change password' });
        return;
      }
      if (!newPassword) {
        setMessage({ type: 'error', text: 'New password is required' });
        return;
      }
      if (newPassword !== confirmPassword) {
        setMessage({ type: 'error', text: 'New passwords do not match' });
        return;
      }
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const payload: Record<string, any> = {
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        job_title: job_title.trim(),
        address_line1: address_line1.trim(),
        address_line2: address_line2.trim(),
        city: city.trim(),
        state: state.trim(),
        postal_code: postal_code.trim(),
        country: country.trim(),
      };

      if (display_name.trim()) {
        payload.display_name = display_name.trim();
      }

      if (currentPassword && newPassword) {
        payload.password = newPassword;
      }

      // Include preferences in custom_fields
      const userCustomFields = (user.custom_fields || {}) as Record<string, unknown>;
      const nextPreferences: UserPreferences = {
        ...preferences,
        logging: loggingPrefs,
      };
      payload.custom_fields = {
        ...userCustomFields,
        preferences: nextPreferences,
      };

      const url = `${apiBase}/api/users/${user.id}`;
      const response = await authFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      // Handle status 0 (network error, CORS issue, etc.)
      if (response.status === 0) {
        throw new Error('Network error: Unable to reach the server. Please check your connection and ensure the backend is running.');
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Failed to update profile: ${response.status} ${response.statusText || errorText}`);
      }

      const data = await response.json().catch(() => ({ status: 'error', message: 'Invalid JSON response' }));

      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to update profile');
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      // Clear password fields after successful save
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Update local user data
      if (data.user) {
        setFirstName(data.user.first_name || '');
        setLastName(data.user.last_name || '');
        setEmail(data.user.email || '');
        setDisplayName(data.user.display_name || '');
        setPhone(data.user.phone || '');
        setJobTitle(data.user.job_title || '');
        setAddressLine1(data.user.address_line1 || '');
        setAddressLine2(data.user.address_line2 || '');
        setCity(data.user.city || '');
        setState(data.user.state || '');
        setPostalCode(data.user.postal_code || '');
        setCountry(data.user.country || 'USA');
        
        // Update preferences from response
        const customFields = data.user.custom_fields || {};
        const mergedPreferences = hydrateUserPreferences(
          customFields.preferences ?? nextPreferences
        );
        setPreferences(mergedPreferences);
        
        // Update AuthContext with new user data
        updateUser({
          ...data.user,
          custom_fields: {
            ...customFields,
            preferences: mergedPreferences,
          },
        });
      }
      
      // Reload page data for sync
      setTimeout(() => {
        loadUserData();
      }, 500);
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update profile' });
    } finally {
      setIsSaving(false);
    }
  }, [apiBase, user?.id, user?.custom_fields, first_name, last_name, email, display_name, phone, job_title, address_line1, address_line2, city, state, postal_code, country, currentPassword, newPassword, confirmPassword, preferences, loggingPrefs, loadUserData, updateUser]);

  const handleClose = useCallback(() => {
    setMessage(null);
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>User Profile</DialogTitle>
            {isAdmin && (
              <Badge variant="default" className="bg-primary text-primary-foreground">
                Admin
              </Badge>
            )}
          </div>
          <DialogDescription id="user-profile-dialog-description">
            Update your profile information and manage your account settings.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !user ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
            <User className="h-12 w-12 mb-4 opacity-50" />
            <p>No user information available.</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[calc(90vh-200px)] pr-4">
            <div className="space-y-6">
              {message && (
                <Alert variant={message.type === 'error' ? 'destructive' : message.type === 'success' ? 'success' : 'default'}>
                  {message.type === 'success' ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>{message.text}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={first_name}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={last_name}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    value={display_name}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Display name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_title">Job Title</Label>
                  <Input
                    id="job_title"
                    value={job_title}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="Job title"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  disabled
                  placeholder="Username (read-only)"
                  className="cursor-not-allowed opacity-70"
                />
              </div>

              <div className="border-t pt-6">
                <h3 className="mb-4 text-sm font-semibold text-foreground">Address</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="address_line1">Address Line 1</Label>
                    <Input
                      id="address_line1"
                      value={address_line1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      value={address_line2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Apartment, suite, etc."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State/Province</Label>
                      <Input
                        id="state"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        placeholder="State"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Postal Code</Label>
                      <Input
                        id="postal_code"
                        value={postal_code}
                        onChange={(e) => setPostalCode(e.target.value)}
                        placeholder="ZIP/Postal code"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="mb-4 text-sm font-semibold text-foreground">Change Password</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave password fields empty to keep your current password.
                  </p>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="mb-4 text-sm font-semibold text-foreground">User Settings</h3>
                <div className="space-y-4">
                  <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Enable universal logging</p>
                        <p className="text-xs text-muted-foreground">
                          Keep warn/error events that you or the app explicitly log so bug reports include enough context.
                        </p>
                      </div>
                      <Switch
                        id="logging-enable"
                        checked={loggingPrefs.enableUniversalLogging}
                        onCheckedChange={(next) =>
                          handleLoggingToggle('enableUniversalLogging', Boolean(next))
                        }
                      />
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Universal logging now stores only warn/error entries. Routine network requests and console output stay in your browser dev tools, so there are no extra capture toggles.
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Automatic bug reports stay limited to uncaught phone-only crashes and error-boundary failures. Desktop and tablet issues still rely on manual reports and browser dev tools.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
